import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Download, ArrowRight, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import BlueprintBackdrop from "../components/BlueprintBackdrop";
import BrandWordmark from "../components/BrandWordmark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <BlueprintBackdrop variant="hero" showIsometric={false} />

      <div className="relative w-full max-w-md z-10">
        <div className="flex justify-center mb-8">
          <BrandWordmark size="md" onClick={() => navigate('/')} />
        </div>

        <div className="glass-card p-8 text-center" data-testid="payment-success-card">
          {status === 'checking' && (
            <>
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-accent border-t-transparent" />
              </div>
              <h2 className="font-display font-black text-2xl text-foreground mb-2">Processing payment</h2>
              <p className="text-sm text-muted-foreground">Please wait while we confirm your payment…</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#C9F0DC] flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-7 h-7 text-[#115E3B]" />
              </div>
              <span className="pre-headline-pill pre-headline-pill-mint mb-3 inline-flex">Paid</span>
              <h2 className="font-display font-black text-3xl text-foreground mb-2">Payment successful</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Your CALGreen Waste Management Plan is ready for download.
              </p>

              {paymentData && (
                <div className="p-4 rounded-2xl bg-muted/60 border border-border mb-6">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Amount paid</p>
                  <p className="font-display font-black text-3xl text-foreground mt-1">
                    ${(paymentData.amount_total / 100).toFixed(2)} <span className="text-base font-normal text-muted-foreground">{paymentData.currency?.toUpperCase()}</span>
                  </p>
                </div>
              )}

              <button
                onClick={() => navigate('/dashboard')}
                className="pill-btn pill-btn-primary w-full justify-center"
                data-testid="go-to-dashboard-btn"
              >
                <Download className="w-4 h-4" />
                Go to dashboard &amp; download
              </button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#FFF1C8] flex items-center justify-center mx-auto mb-6">
                <Clock className="w-7 h-7 text-[#8B5A00]" />
              </div>
              <h2 className="font-display font-black text-2xl text-foreground mb-2">Payment processing</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Your payment is being processed. Please check your dashboard in a few moments.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="pill-btn pill-btn-primary w-full justify-center"
              >
                Go to dashboard
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {(status === 'error' || status === 'expired') && (
            <>
              <div className="w-14 h-14 rounded-full bg-[#FCD9D6] flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-7 h-7 text-[#8B1F1F]" />
              </div>
              <h2 className="font-display font-black text-2xl text-foreground mb-2">
                {status === 'expired' ? 'Session expired' : 'Something went wrong'}
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {status === 'expired'
                  ? 'Your payment session has expired. Please try again.'
                  : "We couldn't confirm your payment. Please check your dashboard or contact support."}
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="pill-btn pill-btn-primary w-full justify-center"
              >
                Go to dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
